import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import SEO from '../components/SEO';

const API = process.env.REACT_APP_API_URL || 'https://dev.arhat.info';

// ── ICONS ─────────────────────────────────────────────────────────────
const Icon = ({ name, size = 18 }) => {
  const icons = {
    globe:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    search:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    server:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>,
    shield:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    zap:     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    route:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>,
    wifi:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>,
    copy:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
    check:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
    alert:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    loader:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
    port:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  };
  return icons[name] || null;
};

// ── SHARED COMPONENTS ─────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '0.5px solid var(--border)',
      borderRadius: 14,
      overflow: 'hidden',
      ...style,
    }}>{children}</div>
  );
}

function CardHead({ icon, title, subtitle }) {
  return (
    <div style={{
      padding: '16px 20px',
      borderBottom: '0.5px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 10,
      background: 'var(--surface2)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9,
        background: 'rgba(53,187,234,.15)',
        border: '0.5px solid rgba(53,187,234,.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--blue)', flexShrink: 0,
      }}><Icon name={icon} size={16}/></div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function Input({ value, onChange, onEnter, placeholder, disabled }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onEnter && onEnter()}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        flex: 1, padding: '9px 13px',
        background: 'var(--input)', border: '0.5px solid var(--border)',
        borderRadius: 8, color: 'var(--white)',
        fontSize: 13, fontFamily: 'var(--mono)',
        outline: 'none',
      }}
    />
  );
}

function Btn({ onClick, loading, children, disabled }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{
      padding: '9px 18px',
      background: 'rgba(53,187,234,.15)',
      border: '0.5px solid rgba(53,187,234,.4)',
      borderRadius: 8, color: 'var(--blue)',
      fontSize: 13, fontWeight: 600,
      cursor: loading || disabled ? 'default' : 'pointer',
      opacity: loading || disabled ? 0.55 : 1,
      fontFamily: 'inherit', whiteSpace: 'nowrap',
      display: 'flex', alignItems: 'center', gap: 6,
      transition: 'all .15s',
    }}>
      {loading ? <Spin/> : children}
    </button>
  );
}

function Spin() {
  return (
    <div style={{
      width: 13, height: 13, borderRadius: '50%',
      border: '2px solid rgba(53,187,234,.3)',
      borderTopColor: 'var(--blue)',
      animation: 'spin .7s linear infinite',
    }}/>
  );
}

function Row({ label, value, mono = false, copyable = false }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: '0.5px solid var(--border-faint)',
      gap: 12,
    }}>
      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          fontSize: 12.5, color: 'var(--white)',
          fontFamily: mono ? 'var(--mono)' : 'inherit',
          textAlign: 'right', wordBreak: 'break-all',
        }}>{String(value)}</span>
        {copyable && (
          <button onClick={copy} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: copied ? '#4ade80' : 'rgba(255,255,255,0.75)',
            padding: 2, flexShrink: 0,
          }}>
            <Icon name={copied ? 'check' : 'copy'} size={13}/>
          </button>
        )}
      </div>
    </div>
  );
}

function Badge({ text, color = 'blue' }) {
  const colors = {
    blue:   { bg: 'rgba(53,187,234,.12)',  border: 'rgba(53,187,234,.3)',  text: '#35BBEA' },
    green:  { bg: 'rgba(74,222,128,.12)',  border: 'rgba(74,222,128,.3)',  text: '#4ade80' },
    red:    { bg: 'rgba(239,68,68,.12)',   border: 'rgba(239,68,68,.3)',   text: '#ef4444' },
    yellow: { bg: 'rgba(251,191,36,.12)',  border: 'rgba(251,191,36,.3)',  text: '#fbbf24' },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 20,
      background: c.bg, border: `0.5px solid ${c.border}`,
      fontSize: 11, fontWeight: 600, color: c.text,
    }}>{text}</span>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: '10px 14px', margin: '12px 20px',
      background: 'rgba(239,68,68,.08)',
      border: '0.5px solid rgba(239,68,68,.3)',
      borderRadius: 8, fontSize: 12.5, color: '#ef4444',
      display: 'flex', gap: 7, alignItems: 'flex-start',
    }}>
      <Icon name="alert" size={14}/> {msg}
    </div>
  );
}

// ── API CALLS ─────────────────────────────────────────────────────────
async function apiFetch(endpoint, params = {}) {
  const url = new URL(`${API}/api/tools/${endpoint}/`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── TOOL 1: MY IP ─────────────────────────────────────────────────────
function MyIPTool() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    apiFetch('myip')
      .then(setData)
      .catch(() => setError('Could not detect your IP. Check your connection.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card>
      <CardHead icon="globe" title="What Is My IP" subtitle="Your public IP address and location"/>
      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ display:'flex', gap:8, alignItems:'center', color:'rgba(255,255,255,0.75)', fontSize:13 }}><Spin/> Detecting your IP…</div>}
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{
              padding: '14px 16px', marginBottom: 16,
              background: 'rgba(53,187,234,.06)',
              border: '0.5px solid rgba(53,187,234,.2)',
              borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginBottom: 4 }}>Your IP Address</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--blue)' }}>
                  {data.ip}
                </div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
                {data.version && <Badge text={`IPv${data.version}`}/>}
                {data.is_vpn && <Badge text="VPN Detected" color="yellow"/>}
                {data.is_proxy && <Badge text="Proxy" color="yellow"/>}
              </div>
            </div>
            <Row label="Country"   value={data.country_name ? `${data.country_flag || ''} ${data.country_name}` : null}/>
            <Row label="Region"    value={data.region}/>
            <Row label="City"      value={data.city}/>
            <Row label="ISP"       value={data.isp}/>
            <Row label="Timezone"  value={data.timezone}/>
            <Row label="Latitude"  value={data.latitude}  mono/>
            <Row label="Longitude" value={data.longitude} mono/>
            <Row label="ASN"       value={data.asn}       mono copyable/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 2: IP LOOKUP ─────────────────────────────────────────────────
function IPLookupTool() {
  const [ip, setIp]         = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!ip.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('iplookup', { ip: ip.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="search" title="IP Lookup" subtitle="Look up any IP address for location and details"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={ip} onChange={setIp} onEnter={lookup} placeholder="Enter IP address (e.g. 8.8.8.8)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>Lookup</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="IP"        value={data.ip}          mono copyable/>
            <Row label="Country"   value={data.country_name ? `${data.country_flag||''} ${data.country_name}` : null}/>
            <Row label="Region"    value={data.region}/>
            <Row label="City"      value={data.city}/>
            <Row label="ISP"       value={data.isp}/>
            <Row label="ASN"       value={data.asn}         mono/>
            <Row label="Timezone"  value={data.timezone}/>
            <Row label="Latitude"  value={data.latitude}    mono/>
            <Row label="Longitude" value={data.longitude}   mono/>
            {data.is_vpn !== undefined && (
              <div style={{ marginTop: 10, display:'flex', gap:6, flexWrap:'wrap' }}>
                <Badge text={data.is_vpn ? 'VPN Detected' : 'Not VPN'} color={data.is_vpn ? 'yellow' : 'green'}/>
                <Badge text={data.is_proxy ? 'Proxy Detected' : 'Not Proxy'} color={data.is_proxy ? 'yellow' : 'green'}/>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 3: HOSTNAME LOOKUP ───────────────────────────────────────────
function HostnameTool() {
  const [host, setHost]     = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!host.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('hostname', { host: host.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="server" title="Hostname Lookup" subtitle="Resolve hostname to IP or IP to hostname"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={host} onChange={setHost} onEnter={lookup} placeholder="Enter hostname or IP (e.g. google.com)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>Resolve</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="Input"       value={data.input}         mono copyable/>
            <Row label="Hostname"    value={data.hostname}      mono copyable/>
            <Row label="IP Address"  value={data.ip}            mono copyable/>
            <Row label="Aliases"     value={data.aliases?.join(', ')}/>
            <Row label="DNS Server"  value={data.dns_server}    mono/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 4: WHOIS ─────────────────────────────────────────────────────
function WhoisTool() {
  const [domain, setDomain] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!domain.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('whois', { domain: domain.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'WHOIS lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="search" title="IP WHOIS Lookup" subtitle="Domain registration and IP ownership information"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={domain} onChange={setDomain} onEnter={lookup} placeholder="Enter domain or IP (e.g. google.com)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>WHOIS</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="Domain"       value={data.domain}          mono copyable/>
            <Row label="Registrar"    value={data.registrar}/>
            <Row label="Created"      value={data.created}/>
            <Row label="Expires"      value={data.expires}/>
            <Row label="Updated"      value={data.updated}/>
            <Row label="Status"       value={data.status}/>
            <Row label="Name Servers" value={data.name_servers?.join(', ')} mono/>
            <Row label="Registrant"   value={data.registrant}/>
            <Row label="Country"      value={data.country}/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 5: BLACKLIST CHECK ───────────────────────────────────────────
function BlacklistTool() {
  const [ip, setIp]         = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const check = async () => {
    if (!ip.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('blacklist', { ip: ip.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Check failed'); }
    finally { setLoading(false); }
  };

  const listed = data?.blacklists?.filter(b => b.listed) || [];
  const clean  = data?.blacklists?.filter(b => !b.listed) || [];

  return (
    <Card>
      <CardHead icon="shield" title="Blacklist Check" subtitle="Check if an IP is listed on spam or abuse blacklists"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={ip} onChange={setIp} onEnter={check} placeholder="Enter IP address to check" disabled={loading}/>
          <Btn onClick={check} loading={loading}>Check</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{ marginBottom: 14, display:'flex', gap:8, alignItems:'center' }}>
              <Badge
                text={listed.length === 0 ? '✓ Clean' : `⚠ Listed on ${listed.length} blacklist${listed.length>1?'s':''}`}
                color={listed.length === 0 ? 'green' : 'red'}
              />
              <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)' }}>
                {data.total_checked} blacklists checked
              </span>
            </div>
            {listed.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Listed on</div>
                {listed.map((b, i) => (
                  <div key={i} style={{ padding: '5px 0', borderBottom: '0.5px solid var(--border-faint)', display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize: 12.5, fontFamily: 'var(--mono)', color: 'var(--white)' }}>{b.name}</span>
                    <Badge text="LISTED" color="red"/>
                  </div>
                ))}
              </div>
            )}
            {clean.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#4ade80', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Clean on</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {clean.map((b, i) => (
                    <span key={i} style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'rgba(255,255,255,0.75)' }}>{b.name}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 6: PORT SCANNER ──────────────────────────────────────────────
function PortScanTool() {
  const [host, setHost]     = useState('');
  const [ports, setPorts]   = useState('80,443,22,21,25,3306,5432,8080');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const scan = async () => {
    if (!host.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('portscan', { host: host.trim(), ports });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Port scan failed'); }
    finally { setLoading(false); }
  };

  const open   = data?.ports?.filter(p => p.status === 'open')   || [];
  const closed = data?.ports?.filter(p => p.status === 'closed') || [];

  return (
    <Card>
      <CardHead icon="port" title="Port Scanner" subtitle="Scan open ports on any host"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', flexDirection:'column', gap: 8, marginBottom: 12 }}>
          <Input value={host} onChange={setHost} placeholder="Enter hostname or IP" disabled={loading}/>
          <div style={{ display:'flex', gap:8 }}>
            <Input value={ports} onChange={setPorts} placeholder="Ports (comma-separated)" disabled={loading}/>
            <Btn onClick={scan} loading={loading}>Scan</Btn>
          </div>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{ marginBottom: 10, display:'flex', gap:8 }}>
              <Badge text={`${open.length} Open`}   color="green"/>
              <Badge text={`${closed.length} Closed`} color="red"/>
            </div>
            {data.ports?.map((p, i) => (
              <div key={i} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                padding: '6px 0', borderBottom: '0.5px solid var(--border-faint)',
              }}>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontFamily:'var(--mono)', fontSize:13, color:'var(--white)', minWidth:45 }}>{p.port}</span>
                  <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>{p.service || ''}</span>
                </div>
                <Badge text={p.status} color={p.status === 'open' ? 'green' : 'red'}/>
              </div>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 7: TRACEROUTE ────────────────────────────────────────────────
function TracerouteTool() {
  const [host, setHost]       = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const trace = async () => {
    if (!host.trim()) return;
    setLoading(true);
    setError('');
    setData(null);

    try {
      // Call hackertarget directly from the browser — bypasses server restrictions
      const res = await fetch(
        `https://api.hackertarget.com/traceroute/?q=${encodeURIComponent(host.trim())}`,
        { signal: AbortSignal.timeout(30000) }
      );
      const text = await res.text();

      if (text.includes('API count') || text.includes('limit')) {
        throw new Error('Daily API limit reached (100/day). Try again tomorrow.');
      }
      if (text.includes('error') || text.includes('invalid')) {
        throw new Error('Could not trace route to that host.');
      }

      // Parse the plain text response
      // Format: "1  router (192.168.1.1)  1.234 ms"
      const lines = text.trim().split('\n');
      const hops  = [];

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const parts = trimmed.split(/\s+/);
        let hopNum = null;

        try { hopNum = parseInt(parts[0]); } catch { continue; }
        if (isNaN(hopNum)) continue;

        let rtt      = null;
        let hopHost  = null;
        let hopIp    = null;

        for (let i = 1; i < parts.length; i++) {
          const p = parts[i];

          // RTT value like "12.345"
          if (!isNaN(parseFloat(p)) && !p.includes('.') === false) {
            rtt = parseFloat(p);
            continue;
          }
          // ms unit — rtt is the previous token
          if (p === 'ms' && i > 1) {
            rtt = parseFloat(parts[i - 1]);
            continue;
          }
          // IP in parens like (8.8.8.8)
          if (p.startsWith('(') && p.endsWith(')')) {
            hopIp = p.slice(1, -1);
            continue;
          }
          // Plain IP address
          if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(p)) {
            hopIp = p;
            continue;
          }
          // Hostname — has dots but not an IP
          if (p.includes('.') && !/^\d/.test(p) && p !== 'ms') {
            hopHost = p;
          }
        }

        // * means no response
        const isTimeout = parts.includes('*') && !hopIp;

        hops.push({
          hop:     hopNum,
          ip:      hopIp,
          host:    hopHost || hopIp,
          rtt:     rtt,
          timeout: isTimeout,
        });
      }

      if (hops.length === 0) {
        throw new Error('No hops returned. The host may be unreachable.');
      }

      setData({ host: host.trim(), hops, total_hops: hops.length });

    } catch (e) {
      setError(e.message || 'Traceroute failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHead
        icon="route"
        title="Traceroute"
        subtitle="Trace the network path to any host"
      />
      <div style={{ padding:'16px 20px' }}>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          <Input
            value={host}
            onChange={setHost}
            onEnter={trace}
            placeholder="Enter hostname or IP (e.g. google.com)"
            disabled={loading}
          />
          <Btn onClick={trace} loading={loading}>Trace</Btn>
        </div>

        {loading && (
          <div style={{
            fontSize:12.5, color:'rgba(255,255,255,0.75)', marginBottom:8,
            display:'flex', alignItems:'center', gap:6,
          }}>
            <Spin/> Tracing route — this takes 15–30 seconds…
          </div>
        )}

        <ErrorMsg msg={error}/>

        {data && (
          <>
            <div style={{
              fontSize:11.5, color:'rgba(255,255,255,0.75)', marginBottom:12
            }}>
              {data.total_hops} hops to <strong style={{ color:'var(--white)' }}>{data.host}</strong>
            </div>

            {/* hop table header */}
            <div style={{
              display:'grid',
              gridTemplateColumns:'32px 80px 1fr 70px',
              gap:10, padding:'4px 0 8px',
              borderBottom:'0.5px solid var(--border)',
              fontSize:10.5, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'.06em',
              color:'rgba(255,255,255,0.75)',
            }}>
              <span>#</span>
              <span>RTT</span>
              <span>Host</span>
              <span>Status</span>
            </div>

            {/* hop rows */}
            {data.hops.map((hop, i) => (
              <div key={i} style={{
                display:'grid',
                gridTemplateColumns:'32px 80px 1fr 70px',
                gap:10, padding:'7px 0',
                borderBottom:'0.5px solid var(--border-faint)',
                alignItems:'center',
              }}>
                <span style={{
                  fontSize:12, fontFamily:'var(--mono)',
                  color:'rgba(255,255,255,0.75)',
                }}>
                  {hop.hop}
                </span>
                <span style={{
                  fontSize:12, fontFamily:'var(--mono)',
                  color: hop.timeout ? 'rgba(255,255,255,0.75)'
                       : hop.rtt < 20  ? '#4ade80'
                       : hop.rtt < 80  ? '#fbbf24'
                       : '#ef4444',
                }}>
                  {hop.timeout ? '*' : hop.rtt ? `${hop.rtt}ms` : '*'}
                </span>
                <span style={{
                  fontSize:12, color: hop.timeout ? 'rgba(255,255,255,0.75)' : 'var(--white)',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                }}>
                  {hop.timeout ? 'Request timed out' : (hop.host || hop.ip || '*')}
                </span>
                <Badge
                  text={hop.timeout ? 'timeout' : 'ok'}
                  color={hop.timeout ? 'yellow' : 'green'}
                />
              </div>
            ))}

            {/* summary */}
            <div style={{
              marginTop:12, padding:'8px 12px',
              background:'rgba(53,187,234,.06)',
              border:'0.5px solid rgba(53,187,234,.2)',
              borderRadius:8, fontSize:11.5, color:'rgba(255,255,255,0.75)',
            }}>
              Total hops: {data.total_hops} ·
              Avg RTT: {
                (() => {
                  const rtts = data.hops.filter(h => h.rtt).map(h => h.rtt);
                  return rtts.length
                    ? `${(rtts.reduce((a,b) => a+b, 0) / rtts.length).toFixed(1)}ms`
                    : 'n/a';
                })()
              } ·
              Timeouts: {data.hops.filter(h => h.timeout).length}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 8: DNS LOOKUP ────────────────────────────────────────────────
function DNSTool() {
  const [domain, setDomain] = useState('');
  const [type, setType]     = useState('A');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!domain.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('dns', { domain: domain.trim(), type });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'DNS lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="wifi" title="DNS Lookup" subtitle="Query DNS records for any domain"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap:'wrap' }}>
          <Input value={domain} onChange={setDomain} onEnter={lookup} placeholder="Enter domain (e.g. google.com)" disabled={loading}/>
          <select value={type} onChange={e => setType(e.target.value)} style={{
            padding:'9px 10px', background:'var(--input)',
            border:'0.5px solid var(--border)', borderRadius:8,
            color:'var(--white)', fontSize:13, fontFamily:'var(--mono)',
          }}>
            {['A','AAAA','MX','TXT','NS','CNAME','SOA','PTR'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Btn onClick={lookup} loading={loading}>Query</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.75)', marginBottom:8 }}>
              {data.records?.length} {type} record{data.records?.length !== 1 ? 's' : ''} for {data.domain}
            </div>
            {data.records?.map((r, i) => (
              <div key={i} style={{
                padding:'7px 10px', marginBottom:5,
                background:'var(--surface2)', borderRadius:7,
                fontFamily:'var(--mono)', fontSize:12,
                color:'var(--white)', wordBreak:'break-all',
                border:'0.5px solid var(--border)',
              }}>
                {typeof r === 'object' ? JSON.stringify(r) : r}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 9: USER AGENT ────────────────────────────────────────────────
function UserAgentTool() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    apiFetch('useragent')
      .then(setData)
      .catch(() => setError('Could not parse user agent.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card>
      <CardHead icon="server" title="User Agent Info" subtitle="Details about your browser and device"/>
      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ display:'flex', gap:8, alignItems:'center', color:'rgba(255,255,255,0.75)', fontSize:13 }}><Spin/> Detecting…</div>}
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{
              padding:'10px 12px', marginBottom:14,
              background:'var(--surface2)', borderRadius:8,
              fontFamily:'var(--mono)', fontSize:11.5, color:'rgba(255,255,255,0.75)',
              wordBreak:'break-all', lineHeight:1.6,
              border:'0.5px solid var(--border)',
            }}>{data.raw}</div>
            <Row label="Browser"  value={data.browser}/>
            <Row label="Version"  value={data.version}/>
            <Row label="OS"       value={data.os}/>
            <Row label="Device"   value={data.device}/>
            <Row label="Engine"   value={data.engine}/>
            <Row label="Mobile"   value={data.is_mobile ? 'Yes' : 'No'}/>
            <Row label="Bot"      value={data.is_bot    ? 'Yes' : 'No'}/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 10: SPEED TEST ───────────────────────────────────────────────
function SpeedTestTool() {
  const [phase,   setPhase]   = useState('idle');
  const [results, setResults] = useState({});
  const [log,     setLog]     = useState([]);
  const [error,   setError]   = useState('');

  const addLog = (msg) => setLog(prev => [...prev, msg]);

  // ── Ping — 10 samples, take median ──────────────────────────────
  const measurePing = async () => {
    setPhase('ping');
    addLog('📡 Measuring ping (10 samples)…');
    const samples = [];
    for (let i = 0; i < 10; i++) {
      const t0  = performance.now();
      await fetch(`${API}/api/tools/ping/?t=${Date.now()}`, { cache: 'no-store' });
      const rtt = performance.now() - t0;
      samples.push(rtt);
    }
    // Sort and take median
    samples.sort((a, b) => a - b);
    const median = samples[Math.floor(samples.length / 2)];
    const ping   = Math.round(median);
    setResults(r => ({ ...r, ping }));
    addLog(`  → Ping: ${ping}ms (median of 10 samples)`);
    return ping;
  };

  // ── Download — 3 parallel connections, 25MB each ────────────────
  const measureDownload = async () => {
    setPhase('download');
    addLog('⬇ Measuring download (3 parallel connections, 25MB each)…');

    const SIZE        = 3_000_000;   // 25MB per connection
    const CONNECTIONS = 3;
    const start       = performance.now();
    let   totalBytes  = 0;

    const fetchOne = async () => {
      const res    = await fetch(
        `${API}/api/tools/speedtest/?size=${SIZE}&t=${Date.now()}`,
        { cache: 'no-store' }
      );
      const buffer = await res.arrayBuffer();
      return buffer.byteLength;
    };

    try {
      const results = await Promise.all(
        Array.from({ length: CONNECTIONS }, fetchOne)
      );
      totalBytes = results.reduce((a, b) => a + b, 0);
    } catch (e) {
      throw new Error(`Download failed: ${e.message}`);
    }

    const elapsed  = (performance.now() - start) / 1000;  // seconds
    const mbps     = parseFloat(((totalBytes * 8) / elapsed / 1_000_000).toFixed(1));
    setResults(r => ({ ...r, download: mbps }));
    addLog(`  → Download: ${mbps} Mbps (${(totalBytes/1_000_000).toFixed(0)}MB in ${elapsed.toFixed(1)}s)`);
    return mbps;
  };

  // ── Upload — send 10MB to server, measure throughput ────────────
  const measureUpload = async () => {
    setPhase('upload');
    addLog('⬆ Measuring upload (10MB)…');

    const SIZE  = 3_000_000;   // 10MB
    // Random data so it can't be compressed
    const data  = new Uint8Array(SIZE);
    crypto.getRandomValues(data.subarray(0, Math.min(SIZE, 65536)));
    // Fill rest with pattern (crypto.getRandomValues has size limit)
    for (let i = 65536; i < SIZE; i++) data[i] = i % 256;
    const blob  = new Blob([data], { type: 'application/octet-stream' });

    const start = performance.now();
    try {
      const res = await fetch(`${API}/api/tools/speedtest/upload/`, {
        method:  'POST',
        body:    blob,
        headers: { 'Content-Type': 'application/octet-stream' },
        cache:   'no-store',
      });
      await res.json();
    } catch (e) {
      throw new Error(`Upload failed: ${e.message}`);
    }

    const elapsed = (performance.now() - start) / 1000;
    const mbps    = parseFloat(((SIZE * 8) / elapsed / 1_000_000).toFixed(1));
    setResults(r => ({ ...r, upload: mbps }));
    addLog(`  → Upload: ${mbps} Mbps (${(SIZE/1_000_000).toFixed(0)}MB in ${elapsed.toFixed(1)}s)`);
    return mbps;
  };

  // ── Run full test ────────────────────────────────────────────────
  const runTest = async () => {
    setPhase('running');
    setResults({});
    setLog([]);
    setError('');
    addLog('Starting speed test…');

    try {
      await measurePing();
      await measureDownload();
      await measureUpload();
      setPhase('done');
      addLog('✅ Test complete');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  // ── Speed rating ─────────────────────────────────────────────────
  const rateSpeed = (mbps, type) => {
    if (type === 'ping') {
      if (mbps < 20)  return { label: 'Excellent', color: '#4ade80' };
      if (mbps < 50)  return { label: 'Good',      color: '#4ade80' };
      if (mbps < 100) return { label: 'Fair',       color: '#fbbf24' };
      return               { label: 'Poor',         color: '#ef4444' };
    }
    if (mbps > 100) return { label: 'Excellent', color: '#4ade80' };
    if (mbps > 25)  return { label: 'Good',      color: '#4ade80' };
    if (mbps > 5)   return { label: 'Fair',       color: '#fbbf24' };
    return               { label: 'Slow',         color: '#ef4444' };
  };

  const metrics = [
    { key: 'ping',     label: 'Ping',     unit: 'ms',   icon: '📡' },
    { key: 'download', label: 'Download', unit: 'Mbps', icon: '⬇' },
    { key: 'upload',   label: 'Upload',   unit: 'Mbps', icon: '⬆' },
  ];

  return (
    <Card>
      <CardHead icon="zap" title="Speed Test"
        subtitle="Tests connection between your browser and this server"/>
      <div style={{ padding: '16px 20px' }}>

        {/* Start button */}
        {phase === 'idle' && (
          <Btn onClick={runTest}>▶ Start Speed Test</Btn>
        )}

        {/* Progress bar */}
        {phase === 'running' || phase === 'ping' ||
         phase === 'download' || phase === 'upload' ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.75)', marginBottom:8 }}>
              {phase === 'ping'     && '📡 Testing ping…'}
              {phase === 'download' && '⬇ Testing download…'}
              {phase === 'upload'   && '⬆ Testing upload…'}
              {phase === 'running'  && '⏳ Starting…'}
            </div>
            <div style={{
              height: 4, background:'var(--border)', borderRadius:2, overflow:'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius:2,
                background: 'linear-gradient(90deg, #35BBEA, #3b82f6)',
                width: phase==='ping' ? '33%' : phase==='download' ? '66%' : '90%',
                transition: 'width .5s ease',
              }}/>
            </div>
          </div>
        ) : null}

        {/* Error */}
        {error && <ErrorMsg msg={error}/>}

        {/* Results */}
        {Object.keys(results).length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: 10, marginBottom: 14,
          }}>
            {metrics.map(({ key, label, unit, icon }) => {
              const val  = results[key];
              const rate = val !== undefined
                ? rateSpeed(val, key)
                : null;
              return (
                <div key={key} style={{
                  padding: '12px 8px', textAlign: 'center',
                  background: 'var(--surface2)', borderRadius: 10,
                  border: `.5px solid ${rate ? rate.color + '44' : 'var(--border)'}`,
                }}>
                  <div style={{ fontSize:11, color:'rgba(255,255,255,0.75)', marginBottom:4 }}>
                    {icon} {label}
                  </div>
                  <div style={{
                    fontSize: 20, fontWeight: 700, fontFamily: 'var(--mono)',
                    color: rate ? rate.color : 'rgba(255,255,255,0.75)',
                  }}>
                    {val !== undefined ? val : <Spin/>}
                  </div>
                  <div style={{ fontSize: 10, color:'rgba(255,255,255,0.75)' }}>{unit}</div>
                  {rate && (
                    <div style={{ fontSize: 10, color: rate.color, marginTop: 3 }}>
                      {rate.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Log */}
        {log.length > 0 && (
          <div style={{
            background: 'var(--surface2)', border: '.5px solid var(--border)',
            borderRadius: 8, padding: '8px 10px', marginBottom: 12,
          }}>
            {log.map((line, i) => (
              <div key={i} style={{
                fontSize: 11.5, fontFamily: 'var(--mono)',
                color: 'rgba(255,255,255,0.75)', lineHeight: 1.7,
              }}>{line}</div>
            ))}
          </div>
        )}

        {/* Re-run */}
        {phase === 'done' && (
          <Btn onClick={runTest}>↺ Run Again</Btn>
        )}

        {/* Note */}
        <div style={{
          marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6,
        }}>
          ⚡ Measures speed between your browser and this server.
          Results reflect your connection to arhatinfo.com specifically.
        </div>
      </div>
    </Card>
  );
}
// ── TABS ──────────────────────────────────────────────────────────────
const TOOLS = [
  { id:'myip',      label:'My IP',        icon:'globe',  component: MyIPTool      },
  { id:'iplookup',  label:'IP Lookup',    icon:'search', component: IPLookupTool  },
  { id:'hostname',  label:'Hostname',     icon:'server', component: HostnameTool  },
  { id:'whois',     label:'WHOIS',        icon:'search', component: WhoisTool     },
  { id:'blacklist', label:'Blacklist',    icon:'shield', component: BlacklistTool },
  { id:'ports',     label:'Port Scan',   icon:'port',   component: PortScanTool  },
  // { id:'traceroute',label:'Traceroute',  icon:'route',  component: TracerouteTool},
  { id:'dns',       label:'DNS Lookup',  icon:'wifi',   component: DNSTool       },
  { id:'useragent', label:'User Agent',  icon:'server', component: UserAgentTool },
  // { id:'speed',     label:'Speed Test',  icon:'zap',    component: SpeedTestTool },
];



const networkToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free Network & IP Tools — Tylented",
  "url":         "https://arhat.info/tools/ip",
  "description": "Free online network tools: IP address lookup with geolocation, DNS record checker, MX record checker, SPF record checker, SSL certificate checker, WHOIS domain lookup, port scanner, traceroute, blacklist check, HTTP headers checker, internet speed test.",
  "applicationCategory": "UtilitiesApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "What Is My IP — public IP address with location",
    "IP Geolocation Lookup — country, city, ISP, ASN",
    "DNS Lookup — A, AAAA, MX, TXT, NS, CNAME records",
    "MX Record Checker",
    "SPF Record Checker",
    "SSL Certificate Checker — expiry, issuer, validity",
    "WHOIS Domain Lookup",
    "Port Scanner",
    "Traceroute",
    "Blacklist Check — 10+ DNS blacklists",
    "HTTP Headers Checker",
    "Internet Speed Test"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};
 
const networkFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "How do I find my public IP address?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Go to arhat.info/tools/ip and your public IP address is shown instantly along with your approximate location, ISP, and whether you are using IPv4 or IPv6. No sign-up or download required."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I check DNS records for a domain?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the free DNS Lookup tool at arhat.info/tools/ip. Enter a domain name and select the record type (A, AAAA, MX, TXT, NS, CNAME). Results appear instantly. Useful for verifying email setup, checking DNS propagation, or troubleshooting website issues."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I check if my IP address is on a spam blacklist?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "The Blacklist Check tool at arhat.info/tools/ip checks your IP address against 10+ major DNS blacklists including Spamhaus, SpamCop, SORBS, Barracuda, and UCEPROTECT. Results show which lists you appear on and links to request removal."
      }
    }
  ]
};
 

const Mapping = {
  'myip': 'myip',
  'ip':     'myip',
  'ip-lookup':  'iplookup',
  'hostname':         'hostname',
  'whois':         'whois',
  'blacklist':       'blacklist',
  'port-scanner':       'ports',
  'dns-lookup':       'dns',
  'user-agent':          'useragent',
  // 'speed-test':          'speed',
};

const ALL_FAQS = {
  myip: [
    {
      q: 'What is my IP address?',
      a: 'Your IP address is a unique number assigned by your Internet Service Provider (ISP) that identifies your device on the internet. This tool shows your current public IP — the address websites see when you visit them.',
    },
    {
      q: 'What is the difference between IPv4 and IPv6?',
      a: 'IPv4 addresses look like 192.168.1.1 (four numbers separated by dots). IPv6 addresses are longer and look like 2001:0db8:85a3::8a2e:0370:7334. IPv6 was introduced because the world ran out of IPv4 addresses.',
    },
    {
      q: 'Can my IP address reveal my exact location?',
      a: 'No — your IP address shows an approximate location (usually your city or region) based on your ISP\'s registered address. It cannot pinpoint your home address. Using a VPN will show the VPN server\'s location instead.',
    },
    {
      q: 'Does my IP address change?',
      a: 'Most home internet connections use a dynamic IP that changes periodically. Business connections often have a static IP that stays the same. Mobile data connections change IP frequently as you move between towers.',
    },
    {
      q: 'What is a VPN and how does it hide my IP?',
      a: 'A VPN (Virtual Private Network) routes your traffic through a server in another location. Websites see the VPN server\'s IP address instead of yours, hiding your real location and identity.',
    },
  ],

  lookup: [
    {
      q: 'What information does an IP lookup show?',
      a: 'An IP lookup returns the approximate geographic location (country, city, region), the ISP or hosting provider, the ASN (Autonomous System Number), and whether the IP is associated with a VPN, proxy, or data center.',
    },
    {
      q: 'How accurate is IP geolocation?',
      a: 'Country-level accuracy is typically 95–99%. City-level accuracy drops to around 70–80%. Rural areas and mobile connections are less accurate. ISPs often register IP blocks in their headquarters city, not where the user actually is.',
    },
    {
      q: 'Why does the IP location show a different city than where I am?',
      a: 'IP geolocation is based on where your ISP registered the IP block, not your actual physical location. Your ISP\'s regional office or data center might be in a different city than you.',
    },
    {
      q: 'What is an ASN?',
      a: 'An Autonomous System Number (ASN) identifies a network operated by a single organization — typically an ISP, university, or large company. For example, AS16509 is Amazon AWS. ASNs are used for internet routing.',
    },
  ],

  dns: [
    {
      q: 'What is a DNS lookup?',
      a: 'DNS (Domain Name System) translates human-readable domain names like arhat.info into IP addresses that computers use to connect. A DNS lookup lets you see all the DNS records associated with a domain.',
    },
    {
      q: 'What are the different types of DNS records?',
      a: 'A records point a domain to an IPv4 address. AAAA records point to IPv6. CNAME records are aliases to other domains. MX records specify mail servers. TXT records store text data like SPF and DKIM. NS records list the authoritative name servers.',
    },
    {
      q: 'Why do DNS changes take time to propagate?',
      a: 'DNS records have a TTL (Time to Live) value measured in seconds. During that window, DNS servers around the world cache the old record. After the TTL expires they fetch the new value. Full global propagation typically takes a few minutes to 48 hours.',
    },
    {
      q: 'What is a TTL in DNS?',
      a: 'TTL (Time to Live) is how long DNS resolvers cache a record before checking again. A TTL of 3600 means the record is cached for 1 hour. Lower TTLs mean faster propagation of changes but more DNS queries.',
    },
  ],

  mx: [
    {
      q: 'What is an MX record?',
      a: 'An MX (Mail Exchange) record tells other mail servers which server handles email for your domain. When someone sends email to you@yourdomain.com, their mail server looks up your MX records to know where to deliver it.',
    },
    {
      q: 'What does the MX priority number mean?',
      a: 'Lower priority numbers are tried first. Priority 10 is tried before priority 20. If the primary mail server is down, the next lowest priority takes over. Most domains have one primary MX with a backup at a higher number.',
    },
    {
      q: 'Why would an MX record lookup fail?',
      a: 'A domain might have no MX records if email isn\'t set up, or if the domain doesn\'t exist. Some domains use their root domain as the mail server without an explicit MX record, which falls back to the A record.',
    },
  ],

  spf: [
    {
      q: 'What is an SPF record?',
      a: 'SPF (Sender Policy Framework) is a DNS TXT record that lists which mail servers are authorised to send email on behalf of your domain. It helps prevent spammers from forging your domain in the From address.',
    },
    {
      q: 'What does "SPF record not found" mean?',
      a: 'It means no TXT record starting with "v=spf1" exists for that domain. Without SPF, receiving mail servers cannot verify whether an email claiming to be from that domain was legitimately sent, which can lead to deliverability issues.',
    },
    {
      q: 'What is the difference between ~all and -all in SPF?',
      a: '"~all" is a soft fail — email from unauthorised servers is accepted but marked as suspicious. "-all" is a hard fail — email from unauthorised servers should be rejected outright. Most senders use ~all for safety during testing.',
    },
  ],

  ssl: [
    {
      q: 'What does an SSL certificate do?',
      a: 'An SSL/TLS certificate encrypts traffic between your browser and a website, preventing eavesdropping. It also verifies the website\'s identity so you know you\'re connecting to the real site and not an impersonator.',
    },
    {
      q: 'What is a certificate expiry date and why does it matter?',
      a: 'SSL certificates expire after a set period (usually 90 days to 1 year). When a certificate expires, browsers show a security warning and block access to the site. This tool shows you the expiry date so you can renew before it lapses.',
    },
    {
      q: 'What is the difference between DV, OV, and EV certificates?',
      a: 'DV (Domain Validated) only verifies domain ownership — fast and cheap. OV (Organisation Validated) also verifies the company identity. EV (Extended Validation) requires the most thorough verification and used to show a green bar in browsers.',
    },
    {
      q: 'What does "certificate not trusted" mean?',
      a: 'It means the certificate was not issued by a Certificate Authority that browsers trust, it has expired, or the domain name on the certificate doesn\'t match the site you\'re visiting.',
    },
  ],

  whois: [
    {
      q: 'What is a WHOIS lookup?',
      a: 'WHOIS is a protocol that returns registration information about a domain — who registered it, when it was created, when it expires, the registrar, and the name servers. Some registrants use privacy protection to hide personal details.',
    },
    {
      q: 'Why is WHOIS information hidden or redacted?',
      a: 'GDPR and similar privacy laws require registrars to redact personal information like names and addresses. Most domain registrars now offer free WHOIS privacy protection that replaces your details with their proxy contact info.',
    },
    {
      q: 'Can I use WHOIS to find out who owns a domain?',
      a: 'Often the registrant\'s details are redacted due to privacy protection. You can still see the registrar, creation date, and name servers. To contact the owner, you can often use the registrar\'s contact relay service.',
    },
    {
      q: 'What is a domain registrar?',
      a: 'A registrar is an accredited company authorised to sell and manage domain registrations. Examples include GoDaddy, Namecheap, Google Domains, and Cloudflare Registrar. They interface with the domain\'s registry on your behalf.',
    },
  ],

  portscan: [
    {
      q: 'What is a port scanner?',
      a: 'A port scanner checks which network ports are open on a server or IP address. Open ports indicate running services — for example port 80 (HTTP), 443 (HTTPS), 22 (SSH), or 3306 (MySQL). This helps diagnose connectivity issues and verify firewall rules.',
    },
    {
      q: 'Is it legal to scan ports?',
      a: 'Scanning your own servers is always legal. Scanning servers you own or have explicit permission to test is legal. Scanning third-party servers without permission is illegal in most jurisdictions and may violate terms of service.',
    },
    {
      q: 'What are common port numbers?',
      a: 'Port 22 = SSH, 25 = SMTP email, 53 = DNS, 80 = HTTP, 110 = POP3, 143 = IMAP, 443 = HTTPS, 3306 = MySQL, 5432 = PostgreSQL, 6379 = Redis, 8080 = alternative HTTP, 27017 = MongoDB.',
    },
    {
      q: 'Why would a port appear closed even though a service is running?',
      a: 'A firewall may be blocking external access to that port. The service might be listening on a non-standard port. The server might only accept connections from specific IP addresses (allowlist), or the service might be bound to localhost only.',
    },
  ],

  traceroute: [
    {
      q: 'What is a traceroute?',
      a: 'Traceroute maps the network path between you and a destination, showing each router (hop) the traffic passes through and how long each hop takes. It\'s used to diagnose where delays or failures are occurring in a network path.',
    },
    {
      q: 'What does an asterisk (*) mean in traceroute output?',
      a: 'An asterisk means a router did not respond to the probe within the timeout — either the router blocks ICMP packets (common), the packet was lost, or the hop is extremely slow. One or two asterisks mid-route is normal and not necessarily a problem.',
    },
    {
      q: 'Why does traceroute show high latency on one hop but the final destination is fast?',
      a: 'Routers often deprioritise ICMP packets (used by traceroute) in favour of regular traffic. A router might respond slowly to traceroute while forwarding your actual traffic quickly. Focus on the final destination\'s latency, not intermediate hops.',
    },
  ],

  blacklist: [
    {
      q: 'What is an IP blacklist?',
      a: 'IP blacklists (also called blocklists or DNSBLs) are databases of IP addresses known to send spam or engage in malicious activity. Mail servers check these lists to decide whether to accept or reject incoming email.',
    },
    {
      q: 'How do I get my IP removed from a blacklist?',
      a: 'First fix the underlying issue (spam, malware, open relay). Then visit the blacklist\'s website and follow their delisting process. Most lists have a self-service removal form. Spamhaus and Barracuda are common ones with online delisting tools.',
    },
    {
      q: 'Why is my IP blacklisted even though I don\'t send spam?',
      a: 'Your IP might have been used by a previous owner who spammed, your network might have a compromised device sending spam without your knowledge, or your ISP\'s entire IP block might be listed due to another customer\'s behaviour.',
    },
    {
      q: 'Does being on a blacklist affect my website?',
      a: 'Blacklists primarily affect email deliverability — your emails may land in spam or be rejected. They generally don\'t affect website traffic. However, some network-level blocklists can affect connectivity for certain users.',
    },
  ],

  headers: [
    {
      q: 'What are HTTP headers?',
      a: 'HTTP headers are key-value pairs sent between browsers and servers with every request and response. They carry metadata like content type, caching rules, security policies, authentication, cookies, and redirect instructions.',
    },
    {
      q: 'What is the X-Frame-Options header?',
      a: 'X-Frame-Options controls whether your site can be embedded in an iframe on another page. "DENY" prevents all embedding. "SAMEORIGIN" allows embedding only from the same domain. This protects against clickjacking attacks.',
    },
    {
      q: 'What is HSTS?',
      a: 'HSTS (HTTP Strict Transport Security) tells browsers to always use HTTPS for your domain, even if the user types http://. Once set, browsers won\'t fall back to HTTP for the duration specified in max-age. This prevents SSL stripping attacks.',
    },
    {
      q: 'What does the Content-Security-Policy header do?',
      a: 'CSP tells browsers which sources of content (scripts, styles, images) are trusted. It\'s one of the most powerful tools for preventing cross-site scripting (XSS) attacks by blocking execution of unauthorised scripts.',
    },
  ],

  speedtest: [
    {
      q: 'How does the speed test work?',
      a: 'The speed test downloads a file from the server and measures how quickly it arrives. It calculates your download speed in Mbps (megabits per second). The result reflects your connection speed to arhat.info\'s server, not your theoretical maximum.',
    },
    {
      q: 'Why is my speed test result lower than my plan speed?',
      a: 'Many factors reduce real-world speeds: WiFi interference, distance from your router, network congestion, the server\'s location, your device\'s hardware, and other devices on your network. Test over ethernet for the most accurate result.',
    },
    {
      q: 'What is a good internet speed?',
      a: 'For general browsing: 10 Mbps is sufficient. HD video streaming: 5–25 Mbps per stream. 4K streaming: 25 Mbps per stream. Video calls: 3–5 Mbps. Working from home: 25–50 Mbps recommended. Online gaming: 3–6 Mbps but low latency matters more than speed.',
    },
    {
      q: 'What is latency and how does it differ from speed?',
      a: 'Speed (bandwidth) is how much data can transfer per second. Latency (ping) is how long a single request takes to get a response. High bandwidth with high latency feels slow for interactive tasks. Gaming and video calls need low latency more than high bandwidth.',
    },
  ],
};

// Generic FAQ shown when no specific one exists
const GENERIC_FAQS = [
  {
    q: 'Are these tools free to use?',
    a: 'Yes — all IP and network tools on arhat.info are completely free. No account or sign-up is required.',
  },
  {
    q: 'Is my data stored when I use these tools?',
    a: 'Network lookups like IP geolocation and DNS queries are made server-side. We do not log or store the domains or IPs you look up.',
  },
  {
    q: 'How often is the geolocation data updated?',
    a: 'Geolocation databases are updated regularly (typically weekly). IP ownership and location can change when ISPs reassign blocks, so there may be occasional inaccuracies.',
  },
];

function FAQSection({ active }) {
  const [open, setOpen] = useState(null);
  const faqs = ALL_FAQS[active] || GENERIC_FAQS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            style={{
              border:       `1px solid ${isOpen ? 'rgba(53,187,234,.35)' : 'var(--border)'}`,
              borderRadius: 10,
              background:   isOpen ? 'rgba(53,187,234,.04)' : 'var(--surface)',
              overflow:     'hidden',
              transition:   'all .2s',
            }}
          >
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:      '100%',
                display:    'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding:    '13px 16px',
                background: 'transparent',
                border:     'none',
                cursor:     'pointer',
                textAlign:  'left',
                gap:        12,
              }}
            >
              <span style={{
                fontSize:   13.5,
                fontWeight: 600,
                color:      isOpen ? 'var(--blue)' : 'var(--white)',
                lineHeight: 1.4,
                transition: 'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:  0,
                width:       20,
                height:      20,
                borderRadius:'50%',
                border:      `1px solid ${isOpen ? 'var(--blue)' : 'var(--border)'}`,
                display:     'flex',
                alignItems:  'center',
                justifyContent:'center',
                fontSize:    13,
                color:       isOpen ? 'var(--blue)' : 'var(--muted)',
                transition:  'all .2s',
                transform:   isOpen ? 'rotate(45deg)' : 'none',
              }}>
                +
              </span>
            </button>

            {isOpen && (
              <div style={{
                padding:    '0 16px 14px',
                fontSize:   13,
                color:      'rgba(255,255,255,.65)',
                lineHeight: 1.75,
                borderTop:  '1px solid rgba(53,187,234,.12)',
                paddingTop: 12,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────
export default function IPTools() {
    const navigate = useNavigate();

  const hostname = window.location.hostname;

// app.dev.example.com => ["app", "dev", "example", "com"]
const parts = hostname.split(".");

const firstSubdomain =
  parts.length > 2 ? parts[0] : null;

console.log(firstSubdomain);

const path = window.location.pathname;

// "/tools/useragent/"
const match = path.match(/^\/tools\/ip\/([^/]+)/);

const toolName = match ? match[1] : null;

useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [networkToolsSchema, networkFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);


console.log(toolName);

  // const [active, setActive] = useState(toolName?toolName:firstSubdomain ? firstSubdomain : 'myip');
          const [active, setActive] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'myip');
  
  const ActiveTool = TOOLS.find(t => t.id === active)?.component || MyIPTool;

  return (
    <>
      <style>{`
        :root {
          --blue: #35BBEA;
          --surface: rgba(255,255,255,.04);
          --surface2: rgba(255,255,255,.02);
          --border: rgba(255,255,255,.09);
          --border-faint: rgba(255,255,255,.05);
          --white: rgba(235,242,255,.92);
          --muted: rgba(255,255,255,.38);
          --input: rgba(255,255,255,.05);
          --mono: 'JetBrains Mono', 'Fira Code', monospace;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        select option { background: #0d1421; color: white; }
        @media (max-width: 640px) {
  .ip-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex-wrap: nowrap !important;
  }
  .ip-tab {
    flex-shrink: 0;
  }
  .ip-result-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
  /* Two-column layouts in tool cards */
  .ip-two-col {
    grid-template-columns: 1fr !important;
  }
}
      `}</style>
 <div className="page">
<SEO
  title="What Is My IP, DNS Lookup, Port Scanner & Network Tools — Free | Tylented"
  description="Free network tools: find your IP address, look up IP geolocation, check DNS records, verify MX and SPF records, check SSL certificates, run WHOIS, scan ports, trace routes, check blacklists and test internet speed. No sign-up required."
  keywords="what is my ip address, ip lookup tool, ip geolocation free, dns lookup tool free, mx record checker, spf record checker online, ssl certificate checker, whois lookup free, port scanner online free, traceroute online, blacklist ip check, internet speed test, http headers checker, network tools free online"
  canonical="https://arhat.info/tools/ip"
  og={{
    title:       "Free IP Lookup, DNS Checker & Network Tools — Tylented",
    description: "12 free network tools: What Is My IP, IP lookup, DNS, MX record, SPF, SSL checker, WHOIS, port scanner, traceroute, blacklist check, speed test. No sign-up.",
    url:         "https://arhat.info/tools/ip",
    image:       "https://arhat.info/og-tools-ip.png",
  }}
/>
    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{ padding: '32px 0' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
            fontWeight: 900, color: 'var(--white)',
            letterSpacing: '-.02em', marginBottom: 6,
          }}>
            🌐 IP Tools
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
            Network utilities — IP lookup, WHOIS, port scan, DNS, traceroute and more
          </p>
        </div>

        {/* Tool tabs */}
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20,
        }}>
          {TOOLS.map(t => (
            <button key={t.id} onClick={() => {navigate(`/tools/ip/${t.id}/`)
            setActive(t.id);
          }} style={{
              padding: '7px 13px',
              border: `0.5px solid ${active === t.id ? 'rgba(53,187,234,.5)' : 'rgba(255,255,255,.1)'}`,
              borderRadius: 8,
              background: active === t.id ? 'rgba(53,187,234,.12)' : 'rgba(255,255,255,.03)',
              color: active === t.id ? 'var(--blue)' : 'rgba(255,255,255,0.75)',
              fontSize: 12.5, fontWeight: active === t.id ? 600 : 400,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 5,
              transition: 'all .15s',
            }}>
              <Icon name={t.icon} size={13}/>
              {t.label}
            </button>
          ))}
        </div>

        {/* Active tool */}
{/* Active tool */}
<ActiveTool/>

{/* FAQ */}
<div style={{ marginTop: 48 }}>
  <h2 style={{
    fontSize: 18, fontWeight: 800, color: 'var(--white)',
    letterSpacing: '-.02em', marginBottom: 6,
  }}>
    Frequently Asked Questions
  </h2>
  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>
    Common questions about IP addresses, DNS, and network tools.
  </p>

  <FAQSection active={active}/>
</div>      
      </div>
      
      </section></div>
      
    </>
  );
}
